// Singleton pattern for loading Google Maps API
let isLoading = false;
let isLoaded = false;
const callbacks = [];

/**
 * Load Google Maps API script only once
 * Returns a promise that resolves when the API is ready
 */
export const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    const apiKey = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey || apiKey === 'your-google-maps-api-key-here') {
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    // If already loaded, resolve immediately
    if (isLoaded && window.google?.maps?.places?.Autocomplete) {
      resolve(window.google);
      return;
    }

    // If currently loading, queue the callback
    if (isLoading) {
      callbacks.push({ resolve, reject });
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script exists, wait for it to load
      isLoading = true;
      waitForGoogleMaps(resolve, reject);
      return;
    }

    // Start loading
    isLoading = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      waitForGoogleMaps(resolve, reject);
    };

    script.onerror = () => {
      isLoading = false;
      const error = new Error('Failed to load Google Maps API');
      reject(error);
      // Reject all queued callbacks
      callbacks.forEach(cb => cb.reject(error));
      callbacks.length = 0;
    };

    document.head.appendChild(script);
  });
};

/**
 * Wait for Google Maps Places library to be fully available
 */
function waitForGoogleMaps(resolve, reject) {
  let attempts = 0;
  const maxAttempts = 50; // 5 seconds max

  const checkInterval = setInterval(() => {
    attempts++;

    if (window.google?.maps?.places?.Autocomplete) {
      clearInterval(checkInterval);
      isLoading = false;
      isLoaded = true;
      
      // Resolve the current promise
      resolve(window.google);
      
      // Resolve all queued callbacks
      callbacks?.forEach(cb => cb?.resolve(window.google));
      callbacks.length = 0;
    } else if (attempts >= maxAttempts) {
      clearInterval(checkInterval);
      isLoading = false;
      const error = new Error('Google Maps API timeout');
      reject(error);
      
      // Reject all queued callbacks
      callbacks?.forEach(cb => cb?.reject(error));
      callbacks.length = 0;
    }
  }, 100);
}

/**
 * Check if Google Maps API is already loaded
 */
export const isGoogleMapsLoaded = () => {
  return isLoaded && window.google?.maps?.places?.Autocomplete;
};
